// Main game loop - P5.js draw() and updateGame()

import type p5 from 'p5';
import {
    setDt, TARGET_FPS, getScreenShake, setScreenShake,
    getTitleTimer, setTitleTimer, getGameState,
    getIsWaveStarting,
    getGalleryLoadingTimer, setGalleryLoadingTimer,
    getPlayer
} from '../../store';
import { updateThreeJS } from '../../features/background/three-renderer';
import { drawBackground } from '../../features/background/background';
import { drawStartScreen } from '../../ui/screens/start-screen';
import { drawShipSelectScreen } from '../../ui/screens/ship-select';
import { drawGalleryScreen } from '../../ui/screens/gallery';
import {
    drawStageStartAnimation,
    updateWaveStartAnimation, drawWaveStartAnimation
} from '../../ui/animations/animations';
import { updateGame } from './game-loop';
import { drawGame } from '../../ui/hud/game-renderer';
import { drawUI } from '../../ui/hud/ui';
import { drawGameOver } from '../../ui/screens/game-over';

/**
 * draw() - P5.jsのメインループ
 * 毎フレーム（60fps）で呼ばれる
 * @param p - P5.jsインスタンス
 */
export function draw(p: p5): void {
    // フレームレート非依存処理：deltaTime倍率を計算（60FPS基準）
    // deltaTimeはミリ秒なので、16.67ms（60FPS）で割って倍率を算出
    const dt = p.deltaTime / (1000 / TARGET_FPS);
    // 極端な値を制限（ラグ対策）
    setDt(p.constrain(dt, 0.1, 3.0));

    // タイトルタイマーの更新
    setTitleTimer(getTitleTimer() + dt);

    // 画面揺れの値を計算（Three.jsとp5.jsで共有）
    let shakeX = 0, shakeY = 0;
    const screenShake = getScreenShake();
    if (screenShake > 0) {
        shakeX = p.random(-screenShake, screenShake);
        shakeY = p.random(-screenShake, screenShake);
        setScreenShake(screenShake * 0.9); // 徐々に減衰
    }

    // Three.jsの更新（地球等） 画面揺れを適用
    updateThreeJS(shakeX, shakeY);

    // p5.jsの画面揺れエフェクトを適用
    if (shakeX !== 0 || shakeY !== 0) {
        p.translate(shakeX, shakeY);
    }

    // 背景を描画（常に描画）
    drawBackground();

    // ゲーム状態に応じて異なる画面を描画
    const gameState = getGameState();
    if (gameState === 'start') {
        // タイトル画面
        drawStartScreen();
    } else if (gameState === 'gallery') {
        // ギャラリー画面
        // ローディングタイマーの減算
        const loadingTimer = getGalleryLoadingTimer();
        if (loadingTimer > 0) {
            setGalleryLoadingTimer(loadingTimer - 1);
        }
        drawGalleryScreen();
    } else if (gameState === 'select') {
        // 機体選択画面
        drawShipSelectScreen();
    } else if (gameState === 'playing') {
        // ゲームプレイ中
        updateGame();
        drawGame();

        const player = getPlayer();
        if (player.state?.name === 'Entry') {
            drawStageStartAnimation();
        }

        // ウェーブ開始演出（操作可能）
        if (getIsWaveStarting()) {
            updateWaveStartAnimation();
            drawWaveStartAnimation();
        }
        drawUI();      // UIを描画
    } else if (gameState === 'gameover') {
        // ゲームオーバー画面
        drawGame();    // 背景としてゲーム画面を表示
        drawGameOver(); // ゲームオーバー画面を重ねて描画
    }
}
